/**
 * Modules & Topics — shared presentational table of the canonical module →
 * chapter → topic hierarchy. Subject/grade are controlled by the consumer (admin
 * refetches server-side; teacher pre-filters client-side to its scope); level +
 * search are filtered here. Admin passes `onRowClick` (edit) + `toolbarActions`
 * (export/import/+New); teacher omits them → read-only.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { SUBJECT_OPTIONS, GRADE_OPTIONS } from '@padcanvas/shared';
import { DataTable, type ColumnDef } from '../components';
import type { CanonModule, ModuleLevel } from './types';
import { formatGradeBoards } from './gradeBoards';
import { hierarchyColumns } from './hierarchyColumns';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const getLevel = (m: CanonModule): ModuleLevel => m.moduleLevel ?? (m.isTopModule ? 'module' : 'chapter');

interface ModuleRow {
  mod: CanonModule; level: ModuleLevel;
  topicName: string; chapterName: string; moduleName: string;
  questionCount: number; teacherQuestionCount: number; sortKey: string;
}

interface Props {
  modules: CanonModule[];
  subject: string;
  onSubjectChange: (s: string) => void;
  grade: string;
  onGradeChange: (g: string) => void;
  /** Limit the subject/grade dropdowns (teacher scope). */
  subjectOptions?: string[];
  gradeOptions?: string[];
  loading?: boolean;
  /** Admin: row click opens the edit modal. Omit → read-only rows. */
  onRowClick?: (m: CanonModule) => void;
  /** Admin: export/import/+New buttons in the page header. */
  toolbarActions?: ReactNode;
  headerTitle?: string;
  totalCount?: number;
  /** Hide the "# Questions" column (e.g. when counts aren't available). */
  hideQuestionColumn?: boolean;
  /** Rendered between the header and the table (e.g. a save-status banner). */
  banner?: ReactNode;
  children?: ReactNode;
}

export function ModulesTableView({
  modules, subject, onSubjectChange, grade, onGradeChange,
  subjectOptions = SUBJECT_OPTIONS as unknown as string[], gradeOptions = GRADE_OPTIONS as unknown as string[],
  loading, onRowClick, toolbarActions, headerTitle = 'Modules & Topics', totalCount, hideQuestionColumn, banner, children,
}: Props) {
  const [filterLevel, setFilterLevel] = useState('');
  const [filterGradeBoard, setFilterGradeBoard] = useState('');
  const [search, setSearch] = useState('');

  const tableRows = useMemo<ModuleRow[]>(() => {
    const byId = new Map<string, CanonModule>();
    for (const m of modules) byId.set(m.id, m);
    const rows: ModuleRow[] = [];
    for (const mod of modules) {
      const level = getLevel(mod);
      let topicName = '', chapterName = '', moduleName = '';
      if (level === 'module') {
        moduleName = mod.displayName;
      } else if (level === 'chapter') {
        chapterName = mod.displayName;
        moduleName = (mod.parentModuleId ? byId.get(mod.parentModuleId) : undefined)?.displayName ?? '';
      } else {
        topicName = mod.displayName;
        const parent = mod.parentModuleId ? byId.get(mod.parentModuleId) : undefined;
        chapterName = parent?.displayName ?? '';
        moduleName = (parent?.parentModuleId ? byId.get(parent.parentModuleId) : undefined)?.displayName ?? '';
      }
      const modSort = level === 'module' ? mod.sortOrder ?? 0
        : level === 'chapter' ? (mod.parentModuleId ? byId.get(mod.parentModuleId)?.sortOrder ?? 0 : 0)
        : (() => { const ch = mod.parentModuleId ? byId.get(mod.parentModuleId) : undefined; return ch?.parentModuleId ? byId.get(ch.parentModuleId)?.sortOrder ?? 0 : 0; })();
      const chSort = level === 'chapter' ? mod.sortOrder ?? 0
        : level === 'topic' ? (mod.parentModuleId ? byId.get(mod.parentModuleId)?.sortOrder ?? 0 : 0) : 0;
      const topSort = level === 'topic' ? mod.sortOrder ?? 0 : 0;
      const sortKey = `${String(modSort).padStart(4, '0')}-${moduleName}-${String(chSort).padStart(4, '0')}-${chapterName}-${String(topSort).padStart(4, '0')}-${topicName}`;
      rows.push({
        mod, level, topicName, chapterName, moduleName,
        questionCount: mod.questionCount ?? 0,
        teacherQuestionCount: mod.teacherQuestionCount ?? 0,
        sortKey,
      });
    }
    rows.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    return rows;
  }, [modules]);

  const filteredRows = useMemo(() => {
    let rows = tableRows;
    if (filterLevel) rows = rows.filter((r) => r.level === filterLevel);
    if (filterGradeBoard) {
      const has = (r: ModuleRow) => !!r.mod.gradeBoards?.length;
      rows = rows.filter((r) => (filterGradeBoard === 'set' ? has(r) : !has(r)));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        r.topicName.toLowerCase().includes(q) || r.chapterName.toLowerCase().includes(q) ||
        r.moduleName.toLowerCase().includes(q) || (r.mod.readableId ?? '').toLowerCase().includes(q));
    }
    return rows;
  }, [tableRows, search, filterLevel, filterGradeBoard]);

  const columns: ColumnDef<ModuleRow>[] = useMemo(() => [
    // Module / Chapter / Topic come from the shared definition — a batch's
    // Curriculum plan renders the same three over a different row type.
    ...hierarchyColumns<ModuleRow>({
      level: (r) => r.level,
      moduleName: (r) => r.moduleName,
      chapterName: (r) => r.chapterName,
      topicName: (r) => r.topicName,
    }),
    { key: 'grades', header: 'Grades', width: '120px', render: (r) => <span className="dc-muted">{(r.mod.grades || []).join(', ')}</span> },
    // Grade × Board supersedes Grades per board — the authoritative "which board teaches
    // this at which grade". Absent ⇒ grade falls back to the question's class.
    { key: 'gradeBoards', header: 'Grade × Board', width: '190px', mobile: 'hide',
      render: (r) => {
        const tuples = formatGradeBoards(r.mod.gradeBoards);
        return tuples
          ? <span className="dc-muted" style={{ fontSize: 11 }} title={tuples}>{tuples}</span>
          : <span className="dc-no-value" title="No tuple — grade falls back to the question's class.">—</span>;
      } },
    ...(hideQuestionColumn ? [] : [
      { key: 'questions', header: '# Questions', width: '100px', mobileLabel: 'Questions', render: (r: ModuleRow) => <span className="dc-count">{r.questionCount}</span> } as ColumnDef<ModuleRow>,
      // Counted apart from the library: teacher-authored rows carry no
      // board-grade pool key, so they never land in `# Questions`.
      { key: 'teacherQuestions', header: 'Teacher Questions', width: '130px', mobile: 'hide', mobileLabel: 'Teacher questions',
        render: (r: ModuleRow) => <span className="dc-count" style={{ color: r.teacherQuestionCount ? undefined : 'var(--text-tertiary)' }}>{r.teacherQuestionCount}</span> } as ColumnDef<ModuleRow>,
    ]),
    { key: 'level', header: 'Level', width: '80px', mobile: 'accessory', render: (r) => <span className={`dc-pill dc-pill--${r.level === 'module' ? 'prod' : r.level === 'chapter' ? 'beta' : 'devtesting'}`}>{r.level}</span> },
    { key: 'order', header: 'Order', width: '70px', mobile: 'hide', render: (r) => <span className="dc-muted">{r.mod.sortOrder ?? 0}</span> },
  ], [hideQuestionColumn]);

  return (
    <div className="modules-page">
      <div className="page-header">
        <div className="header-left">
          <h1>{headerTitle}</h1>
          {totalCount !== undefined && <span className="total-count">{totalCount} total modules</span>}
        </div>
        {toolbarActions && <div className="header-actions" style={{ display: 'flex', gap: 8 }}>{toolbarActions}</div>}
      </div>

      {banner}

      <DataTable
        columns={columns}
        data={filteredRows}
        rowKey={(r) => r.mod.id}
        onRowClick={onRowClick ? (r) => onRowClick(r.mod) : undefined}
        rowClassName={(r) => (r.mod.isActive === false ? 'dt-row--disabled' : '')}
        loading={loading}
        emptyMessage="No modules found"
        compact
        mobileCards
        toolbar={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={subject} onChange={(e) => onSubjectChange(e.target.value)} className="filter-select">
              {subjectOptions.map((s) => <option key={s} value={s}>{cap(s)}</option>)}
            </select>
            <select value={grade} onChange={(e) => onGradeChange(e.target.value)} className="filter-select">
              <option value="">All Grades</option>
              {gradeOptions.map((g) => <option key={g} value={g}>Class {g}</option>)}
            </select>
            <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)} className="filter-select">
              <option value="">All Levels</option>
              <option value="module">Module</option>
              <option value="chapter">Chapter</option>
              <option value="topic">Topic</option>
            </select>
            <select value={filterGradeBoard} onChange={(e) => setFilterGradeBoard(e.target.value)} className="filter-select">
              <option value="">All Grade × Board</option>
              <option value="set">Has Grade × Board</option>
              <option value="unset">No Grade × Board</option>
            </select>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search modules, chapters, topics..." className="dt-search-input" style={{ minWidth: 200 }} />
          </div>
        }
      />

      {children}
    </div>
  );
}
